import * as React from "react";
import { ChildrenRender, Fields } from "./select.typings";
import { SGBSColor } from "../../common/common.typings";
import Highlight from "../Highlight/Highlight";

export interface OptionProps<T> extends ChildrenRender<T> {
  item: T;
  readOnlyItems?: T[];
  selectedItems: T[];
  idField: Fields<T>;
  labelField: Fields<T>;
  isSuggestionField?: Fields<T>;
  searchTerm: string;
  color?: SGBSColor;
  singleSelect?: boolean;
  onSelect: (element: T, isSelected: boolean, isReadOnly: boolean) => void;
  onRemoveSuggestion?: (item: T) => void;
}

enum IconType {
  add = "add",
  remove = "remove",
  close = "close",
}

export function Option<T>({
  item,
  selectedItems,
  idField,
  labelField,
  isSuggestionField,
  searchTerm,
  onSelect,
  children,
  onRemoveSuggestion,
  readOnlyItems,
  color = "primary",
  singleSelect,
}: OptionProps<T>): React.ReactElement | null {
  const isSelected = selectedItems.filter((element) => element[`${idField}`] === item[`${idField}`]).length > 0;
  const isReadOnly = (readOnlyItems ?? []).filter((element) => element[`${idField}`] === item[`${idField}`]).length > 0;
  const terms = React.useMemo(() => (searchTerm ? searchTerm.split(" ") : []), [searchTerm]);
  const withHighlight = React.useCallback(
    (text: string | number, highlightClassName = "font-weight-medium"): React.ReactElement => {
      const normalizedText = typeof text === "number" ? text?.toString?.() : text;
      return <Highlight text={normalizedText} term={terms} highlightClassName={highlightClassName} />;
    },
    [terms]
  );
  const handleOnChange = (): void => {
    onSelect(item, isSelected, isReadOnly);
  };

  const classNames = `d-flex list-group-item border-0 mt-0 list-group-item-action list-group-item-${
    isSelected && !isReadOnly ? color : "primary"
  } ${isSelected && !isReadOnly ? "active" : ""} ${isReadOnly ? "disabled" : ""}`;

  const handleOnRemoveSuggestion = (e: any) => {
    if (iconName === IconType.close) {
      e.stopPropagation();
      onRemoveSuggestion?.(item);
    }
  };

  const isSuggestion = item[`${isSuggestionField}`];
  const iconName = isSelected ? IconType.remove : isSuggestion ? IconType.close : IconType.add;

  const displayIcon = !isReadOnly && (!singleSelect || isSuggestion);
  return (
    <div
      onClick={handleOnChange}
      style={!isReadOnly ? { cursor: "pointer" } : {}}
      title={item[`${labelField}`]}
      className={classNames}
    >
      <div className="w-100">
        {children
          ? children(item, { withHighlight, isReadOnly, isSelected, onChange: handleOnChange })
          : withHighlight(item[`${labelField}`])}
      </div>
      {displayIcon && (
        <em
          className="text-secondary icon ml-2"
          style={{ fontSize: "1.2rem", lineHeight: 1 }}
          onClick={handleOnRemoveSuggestion}
        >
          {iconName}
        </em>
      )}
    </div>
  );
}
